import cx from 'classnames';
import {useCallback, useContext, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import Iframe from 'react-iframe';
import useResizeObserver from 'use-resize-observer';

import {videoTokenApi} from 'api';
import {Button, Icon, Loader, notifier, SessionWebRTCRoom, Subtitles, TwilioSip, VideoOverlayContent} from 'components';
import {LOGO_ICON_PATH} from 'constants/images';
import {UserContext, UserSessionContext} from 'context';
import {AudioStreamErrorCode, SttClientState, VideoPinParticipant} from 'enums';
import {useUserSession, useMediaDevices, useAudioStreamRecord} from 'hooks';
import {ScreenSharingActivityStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey, getScreenSharingPresenterId, getWebRTCRoomName, mediaDevicesUtils, sessionUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;

const OnHoldView: React.FC = () => {
    const {t} = useTranslation();
    const {toggleOnHold} = useContext(UserSessionContext);
    const {canManageSession} = useUserSession();
    return (
        <div className='tw-flex-center tw-flex-col tw-text-default-dimmed tw-my-4'>
            <div className='tw-ts-h2 tw-mb-4'>{t(T_KEYS.LABELS.IS_ON_HOLD)}</div>
            {canManageSession && <div className='tw-ts-h5'>{t(T_KEYS.LABELS.RESUME)}</div>}
            {!canManageSession && <div className='tw-ts-h5'>{t(T_KEYS.LABELS.WAIT_RESUME)}</div>}
            <Icon name='pause-circle' size='8xl' thickness='xs' className='tw-my-10' color='default-dimmed' />
            {canManageSession &&
                <Button color='primary' width='large' rounded className='tw-text-lg' onClick={toggleOnHold}>
                    {t(T_KEYS.ACTIONS.RESUME_SESSION)}
                </Button>}
        </div>
    );
};

const InviteView: React.FC = () => {
    const {t} = useTranslation();
    const {openInviteModal} = useContext(UserSessionContext);

    return (
        <div className='tw-flex-center tw-flex-col tw-my-4'>
            <div className='tw-flex-center tw-relative tw-w-30 tw-h-30 tw-border tw-border-default tw-rounded-full'>
                <Loader />
                <Icon name='client-participant' size='6xl' thickness='xs' color='default-dimmed' />
            </div>
            <div className='tw-ts-h5 tw-my-8'>{t(T_KEYS.LABELS.INVITE)}</div>
            <Button
                color='primary'
                width='large'
                testAttribute={{id: 'invite-client-button'}}
                rounded
                className='tw-text-lg'
                onClick={() => openInviteModal()}
            >
                {t(T_KEYS.ACTIONS.INVITE)}
            </Button>
        </div>
    );
};

export const InSessionContent: React.FC = () => {
    const {t} = useTranslation();
    const {
        session, audioVideoActivity, videoMinimized, screenSharingActivity, coBrowsingActivity, sipActivity, audioStreamEnabled,
        isOnHold, isInSession, screenSharingActivityStatus, participantId, isStarted, audioActivity, videoPinEnabled, subtitlesEnabled,
        transcriptionLanguage, participantIds, audioVideoView, localParticipant, audioVideoAsColumn
    } = useUserSession();
    const contentRef = useRef<HTMLDivElement>(null);
    const contentRefRect = useResizeObserver<HTMLDivElement>({ref: contentRef});
    const {
        audioEnabled, videoEnabled, fullScreenVideo, sidebarView, virtualBackgroundConfig, videoDevices, audioDevices, videoPinParticipant,
        pinnedParticipantId, tracks,
        onStopScreenSharing, notifyWebRTCStartSuccess, notifyWebRTCStartFailed, setScreenSharingActivityStatus,
        onRemoteNetworkQualityLevelChange, onLocalNetworkQualityLevelChange, onToggleFullScreenVideo, onWebRTCConnectError,
        handleVideoPinParticipantUpdate, setMediaDevices, setTracks, setAudioStreamState
    } = useContext(UserSessionContext);
    const {socket} = useContext(UserContext);
    const presenterId = useMemo(() => getScreenSharingPresenterId(session), [session]);
    const webRTCRoomName = useMemo(() => getWebRTCRoomName(session), [session]);

    const [mediaDevices] = useMediaDevices();
    const mediaConstraints = useMemo(() => ({
        ...(Boolean(audioActivity || audioVideoActivity) &&
            {audioinput: mediaDevicesUtils.getMediaTrackConstraintSet(mediaDevices?.audioinput)}),
        ...(Boolean(audioVideoActivity) &&
            {videoinput: mediaDevicesUtils.getVideoTrackConstraintSet(mediaDevices?.videoinput, mediaDevices?.facingMode)}),
        ...(Boolean(audioActivity || audioVideoActivity) && {audiooutput: mediaDevices?.audiooutput}),
    }), [
        audioActivity, audioVideoActivity, mediaDevices?.audioinput,
        mediaDevices?.audiooutput, mediaDevices?.facingMode, mediaDevices?.videoinput
    ]);

    const shouldRecordAudioStream = useMemo(() =>
        audioStreamEnabled && isInSession && socket?.connected && (!!audioVideoActivity || !!audioActivity),
    [audioStreamEnabled, audioActivity, audioVideoActivity, isInSession, socket?.connected]);

    const handleAudioStreamError = useCallback((error: {code: AudioStreamErrorCode}) => {
        if (error?.code) {
            setAudioStreamState(SttClientState.Failed);
            const detailsTKey = buildTranslationKey(error.code);
            notifier.error({
                title: t(T_KEYS.AUDIO_STREAM.ERRORS.TITLE),
                details: t(T_KEYS.AUDIO_STREAM.ERRORS[detailsTKey])
            });
        }
    }, [t, setAudioStreamState]);

    useAudioStreamRecord({
        shouldRecord: shouldRecordAudioStream,
        socket,
        sessionId: session.id,
        participantId,
        activityId: audioActivity?.id || audioVideoActivity?.id,
        tracks,
        transcriptionLanguage,
        onError: handleAudioStreamError,
        onStateChange: setAudioStreamState
    });

    const overLocalVideoContent = useMemo(() => (
        <VideoOverlayContent
            participant={VideoPinParticipant.Local}
            pinnedParticipant={videoPinParticipant}
            videoPinEnabled={videoPinEnabled}
            onVideoPinParticipantUpdate={handleVideoPinParticipantUpdate}
        />
    ), [videoPinEnabled, videoPinParticipant, handleVideoPinParticipantUpdate]);

    const overRemoteVideoContent = useMemo(() => (
        <VideoOverlayContent
            participant={VideoPinParticipant.Remote}
            pinnedParticipant={videoPinParticipant}
            videoPinEnabled={videoPinEnabled}
            onVideoPinParticipantUpdate={handleVideoPinParticipantUpdate}
        />
    ), [videoPinEnabled, videoPinParticipant, handleVideoPinParticipantUpdate]);

    const renderOverParticipantContent = useCallback((contentParticipantId: string) => {
        const participant = contentParticipantId === localParticipant?.id
            ? VideoPinParticipant.Local
            : VideoPinParticipant.Remote;

        const participantName = sessionUtils.getParticipantNameWithMeById(t, session, contentParticipantId);

        return (
            <VideoOverlayContent
                participant={participant}
                participantId={contentParticipantId}
                pinnedParticipant={videoPinParticipant}
                pinnedParticipantId={pinnedParticipantId}
                videoPinEnabled={videoPinEnabled && !audioVideoAsColumn}
                participantName={participantName}
                onVideoPinParticipantUpdate={handleVideoPinParticipantUpdate}
            />
        );
    }, [videoPinEnabled, videoPinParticipant, pinnedParticipantId, localParticipant, audioVideoAsColumn,
        handleVideoPinParticipantUpdate, t, session]);

    const videoTokenParams = useMemo(() => ({
        sessionId: session?.id,
        participantId,
    }), [participantId, session]);

    return (
        <div className='tw-flex tw-h-full tw-bg-layout'>
            <div ref={contentRef} className='tw-flex-center tw-h-full tw-w-full tw-overflow-auto tw-relative'>
                {!isStarted && <InviteView />}
                {isOnHold && <OnHoldView />}
                {!isOnHold && isStarted && !audioVideoActivity &&
                    <div className='tw-flex-center tw-flex-col tw-absolute tw-inset-0 tw-p-8'>
                        <img src={LOGO_ICON_PATH} alt='Logo' className='tw-max-h-full' />
                    </div>}
                <SessionWebRTCRoom
                    participantId={participantId}
                    participantIds={participantIds}
                    videoPinParticipant={videoPinParticipant}
                    roomName={webRTCRoomName}
                    hidden={isOnHold}
                    getTwilioTokenFn={() => videoTokenApi.getAgentTwilioVideoToken(videoTokenParams)}
                    getKurentoTokenFn={() => videoTokenApi.getAgentInternalWebRTCToken(videoTokenParams)}
                    mediaConstraints={mediaConstraints}
                    videoDevices={videoDevices}
                    audioDevices={audioDevices}
                    audioEnabled={audioEnabled}
                    videoEnabled={videoEnabled}
                    videoMinimized={videoMinimized}
                    fullScreenVideo={fullScreenVideo}
                    isScreenSharingPresenter={presenterId === participantId}
                    audioActivity={audioActivity}
                    audioVideoActivity={audioVideoActivity}
                    screenSharingActivity={screenSharingActivity}
                    screenSharingActivityStatus={screenSharingActivityStatus}
                    parentRect={contentRefRect}
                    overLocalVideoContent={overLocalVideoContent}
                    overRemoteVideoContent={overRemoteVideoContent}
                    virtualBackgroundConfig={virtualBackgroundConfig}
                    screenSharingMessageClassName='tw-absolute tw-bottom-6'
                    videoWrapperClassName='tw-absolute tw-inset-0 tw-flex-center'
                    defaultView={audioVideoView}
                    pinnedParticipantId={pinnedParticipantId}
                    audioVideoAsColumn={audioVideoAsColumn}
                    renderOverParticipantContent={renderOverParticipantContent}
                    notifyWebRTCStartSuccess={notifyWebRTCStartSuccess}
                    notifyWebRTCStartFailed={notifyWebRTCStartFailed}
                    onLocalNetworkQualityLevelChange={onLocalNetworkQualityLevelChange}
                    onRemoteNetworkQualityLevelChange={onRemoteNetworkQualityLevelChange}
                    onToggleFullScreenVideo={onToggleFullScreenVideo}
                    onEnumerateMediaDevices={setMediaDevices}
                    onEnumerateTracks={setTracks}
                    onStartScreenSharing={() => setScreenSharingActivityStatus(ScreenSharingActivityStatus.Accepted)}
                    onStopScreenSharing={onStopScreenSharing}
                    onWebRTCConnectError={onWebRTCConnectError}
                />
                {subtitlesEnabled && <Subtitles session={session} socket={socket} activity={audioVideoActivity || audioActivity} />}
                {sipActivity && !isOnHold && (
                    <TwilioSip
                        sessionId={session?.id}
                        conference={sipActivity.data?.conference}
                        voiceAgent={sipActivity.data?.voiceAgent}
                        audioEnabled={audioEnabled}
                    />
                )}
                {coBrowsingActivity && !isOnHold &&
                    <Iframe
                        className={cx('tw-border-default tw-border-t tw-border-b', {
                            'tw-border-r': sidebarView,
                            'tw-left-0': audioVideoAsColumn
                        })}
                        url={coBrowsingActivity.data.link}
                        position='absolute'
                        allow='autoplay'
                        height='100%'
                        width={audioVideoAsColumn ? '75%' : '100%'}
                    />}
            </div>
        </div>
    );
};
